import * as PopperJS from '@popperjs/core';
import classNames from 'classnames';
import { FC, MouseEventHandler, PropsWithChildren, ReactElement, useState } from 'react';
import { Modifier, Popper } from 'react-popper';

import { ClickOutside } from '../ClickOutside/ClickOutside';
import { DisableScroll } from '../DisableScroll/DisableScroll';
import { Portal } from '../Portal/Portal';
import { usePortalContainer } from '../Portal/PortalContainerContext';
import { TUITheme } from '../types';
import styles from './Popover.styles.m.css';
import { TPopoverModifiers } from './Popover.types';
import { getPlacement } from './utils/getPlacement';

const fullWidthModifier: Modifier<'sameWidth', any> = {
  name: 'sameWidth',
  enabled: true,
  phase: 'beforeWrite',
  requires: ['computeStyles'],
  fn: ({ state }) => {
    state.styles.popper.width = `${state.rects.reference.width}px`;
  },
  effect: ({ state }) => {
    const reference = state.elements.reference as HTMLElement;
    if (reference) {
      state.elements.popper.style.width = `${reference.offsetWidth}px`;
    }
  },
};

interface IPopoverProps {
  isOpened: boolean;
  onClose(e: MouseEvent): void;
  reference: HTMLElement | null;
  clickableReference?: HTMLElement | null;
  placement?: PopperJS.Placement;
  modifiers?: TPopoverModifiers;
  isScrollEnabled?: boolean;
  theme?: TUITheme;
  className?: string;
  isFullWidth?: boolean;
  onMouseEnter?: MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: MouseEventHandler<HTMLDivElement>;
}

export const Popover: FC<PropsWithChildren<IPopoverProps>> = (props) => {
  const { portalContainerRef } = usePortalContainer();
  const [popperRef, setRef] = useState<HTMLElement>();
  const {
    isOpened,
    onClose,
    reference,
    clickableReference = null,
    placement,
    modifiers = [],
    children,
    isScrollEnabled = false,
    theme,
    className,
    isFullWidth,
    ...rest
  } = props;

  if (!isOpened) {
    return null;
  }

  return (
    <Portal containerRef={portalContainerRef}>
      <ClickOutside
        reference={popperRef}
        target={[reference, clickableReference]}
        onClickOutside={onClose}
      >
        <Popper
          innerRef={(node): void => setRef(node)}
          referenceElement={reference || undefined}
          placement={placement && getPlacement(placement)}
          modifiers={[
            {
              name: 'offset',
              options: { offset: [0, 8] },
            },
            ...(isFullWidth ? [fullWidthModifier] : []),
            ...modifiers,
          ]}
        >
          {({ ref, style, placement }): ReactElement => (
            <div
              ref={ref}
              style={style}
              className={classNames(styles.popover, styles[`popover--${theme}`], className)}
              data-placement={placement}
              {...rest}
            >
              {children}
            </div>
          )}
        </Popper>
      </ClickOutside>
      {!isScrollEnabled && <DisableScroll node={popperRef} />}
    </Portal>
  );
};
